/**
 * Boolean union of all placed object instances for Object along Path export.
 * Requires DOM (mounted SVG) for geometry and getCTM().
 */

import ClipperLib from "clipper-lib";
import { withMountedPathSvg } from "./svgUtils";

const CLIPPER_SCALE = 100_000;

function clipperPathToPathD(path: { X: number; Y: number }[]): string {
    if (path.length < 2) return "";
    const fmt = (n: number) => {
        const x = n / CLIPPER_SCALE;
        const s = x.toFixed(6);
        return s.replace(/\.?0+$/, "") || "0";
    };
    let d = `M${fmt(path[0].X)} ${fmt(path[0].Y)}`;
    for (let i = 1; i < path.length; i++) {
        d += `L${fmt(path[i].X)} ${fmt(path[i].Y)}`;
    }
    d += "Z";
    return d;
}

function toClipperPath(pts: [number, number][]): unknown[] {
    return pts.map(
        ([x, y]) =>
            new ClipperLib.IntPoint2(
                Math.round(x * CLIPPER_SCALE),
                Math.round(y * CLIPPER_SCALE)
            )
    );
}

function hasFillForUnion(el: SVGGeometryElement): boolean {
    const cs = getComputedStyle(el);
    const fill = cs.fill;
    if (fill === "none") return false;
    if (fill === "transparent") return false;
    const m = /^rgba?\(\s*[\d.]+\s*,\s*[\d.]+\s*,\s*[\d.]+\s*,\s*([\d.]+)\s*\)/.exec(
        fill
    );
    if (m && Number.parseFloat(m[1]) === 0) return false;
    return true;
}

function transformPoint(
    svg: SVGSVGElement,
    m: DOMMatrix,
    x: number,
    y: number
): [number, number] {
    const p = svg.createSVGPoint();
    p.x = x;
    p.y = y;
    const tp = p.matrixTransform(m);
    return [tp.x, tp.y];
}

function rectToPolygon(el: SVGRectElement, svg: SVGSVGElement): [number, number][] | null {
    const m = el.getCTM();
    if (!m) return null;
    const x = el.x.baseVal.value;
    const y = el.y.baseVal.value;
    const w = el.width.baseVal.value;
    const h = el.height.baseVal.value;
    const corners: [number, number][] = [
        [x, y],
        [x + w, y],
        [x + w, y + h],
        [x, y + h],
    ];
    return corners.map(([cx, cy]) => transformPoint(svg, m, cx, cy));
}

function circleToPolygon(el: SVGCircleElement, svg: SVGSVGElement): [number, number][] | null {
    const m = el.getCTM();
    if (!m) return null;
    const cx = el.cx.baseVal.value;
    const cy = el.cy.baseVal.value;
    const r = el.r.baseVal.value;
    const seg = 36;
    const pts: [number, number][] = [];
    for (let i = 0; i < seg; i++) {
        const a = (i / seg) * Math.PI * 2;
        pts.push(
            transformPoint(svg, m, cx + r * Math.cos(a), cy + r * Math.sin(a))
        );
    }
    return pts;
}

function ellipseToPolygon(el: SVGEllipseElement, svg: SVGSVGElement): [number, number][] | null {
    const m = el.getCTM();
    if (!m) return null;
    const cx = el.cx.baseVal.value;
    const cy = el.cy.baseVal.value;
    const rx = el.rx.baseVal.value;
    const ry = el.ry.baseVal.value;
    const seg = 36;
    const pts: [number, number][] = [];
    for (let i = 0; i < seg; i++) {
        const a = (i / seg) * Math.PI * 2;
        pts.push(
            transformPoint(svg, m, cx + rx * Math.cos(a), cy + ry * Math.sin(a))
        );
    }
    return pts;
}

function parsePointsAttr(points: string | null): [number, number][] {
    if (!points) return [];
    const nums = points.trim().split(/[\s,]+/).map(Number);
    const out: [number, number][] = [];
    for (let i = 0; i + 1 < nums.length; i += 2) {
        if (Number.isFinite(nums[i]) && Number.isFinite(nums[i + 1])) {
            out.push([nums[i], nums[i + 1]]);
        }
    }
    return out;
}

function polygonOrPolylineToPolygon(
    el: SVGPolygonElement | SVGPolylineElement,
    svg: SVGSVGElement,
    close: boolean
): [number, number][] | null {
    const m = el.getCTM();
    if (!m) return null;
    let pts = parsePointsAttr(el.getAttribute("points"));
    if (pts.length < 2) return null;
    if (close && pts.length >= 2) {
        const f = pts[0];
        const l = pts[pts.length - 1];
        if (f[0] !== l[0] || f[1] !== l[1]) {
            pts = [...pts, [f[0], f[1]]];
        }
    }
    if (pts.length < 3) return null;
    return pts.map(([x, y]) => transformPoint(svg, m, x, y));
}

function pathToPolygon(el: SVGPathElement, svg: SVGSVGElement): [number, number][] | null {
    const m = el.getCTM();
    if (!m) return null;
    const len = el.getTotalLength();
    if (len < 1e-6) return null;
    const d = el.getAttribute("d")?.trim() ?? "";
    const closed = /z\s*$/i.test(d);
    const steps = Math.max(8, Math.min(512, Math.ceil(len / 1.5)));
    const pts: [number, number][] = [];
    const p = svg.createSVGPoint();
    for (let i = 0; i < steps; i++) {
        const lp = el.getPointAtLength((len * i) / steps);
        p.x = lp.x;
        p.y = lp.y;
        const tp = p.matrixTransform(m);
        pts.push([tp.x, tp.y]);
    }
    if (closed && pts.length > 2) {
        const f = pts[0];
        const l = pts[pts.length - 1];
        if (Math.hypot(f[0] - l[0], f[1] - l[1]) < 1e-3) {
            pts.pop();
        }
    }
    if (pts.length < 3) return null;
    return pts;
}

function collectPolygonsFromMountedObject(root: SVGSVGElement): any[] {
    const out: any[] = [];
    const sel = root.querySelectorAll<
        SVGPathElement | SVGRectElement | SVGCircleElement | SVGEllipseElement | SVGPolygonElement | SVGPolylineElement
    >("path, rect, circle, ellipse, polygon, polyline");

    for (let i = 0; i < sel.length; i++) {
        const el = sel[i];
        if (!hasFillForUnion(el)) continue;

        let ring: [number, number][] | null = null;
        if (el instanceof SVGPathElement) {
            ring = pathToPolygon(el, root);
        } else if (el instanceof SVGRectElement) {
            ring = rectToPolygon(el, root);
        } else if (el instanceof SVGCircleElement) {
            ring = circleToPolygon(el, root);
        } else if (el instanceof SVGEllipseElement) {
            ring = ellipseToPolygon(el, root);
        } else if (el instanceof SVGPolygonElement) {
            ring = polygonOrPolylineToPolygon(el, root, true);
        } else if (el instanceof SVGPolylineElement) {
            ring = polygonOrPolylineToPolygon(el, root, true);
        }

        if (ring && ring.length >= 3) {
            out.push(toClipperPath(ring));
        }
    }

    return out;
}

function extractDefaultFillFromInnerSvg(innerContent: string): string {
    const m = innerContent.match(
        /fill\s*=\s*["']([^"']+)["']/i
    );
    if (m && m[1] && m[1].toLowerCase() !== "none") {
        return m[1];
    }
    return "#000000";
}

export interface UnionPlacementPayload {
    transform: string;
    objectContent: string;
    viewBox: string;
}

export interface UnionPlacementResult {
    pathD: string;
    fill: string;
}

/** Boolean-union all transformed object instances into one compound path. */
export function unionPlacementPayloads(options: {
    placements: UnionPlacementPayload[];
    /** Used when no fill is found on object SVG. */
    defaultFill?: string;
}): UnionPlacementResult | null {
    const { placements, defaultFill = "#000000" } = options;
    if (placements.length === 0) return null;

    const allPaths: any[] = [];

    for (const pl of placements) {
        const svgMarkup = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${pl.viewBox}"><g transform="${pl.transform}">${pl.objectContent}</g></svg>`;

        const mounted = withMountedPathSvg(svgMarkup, (root) =>
            collectPolygonsFromMountedObject(root),
        );
        if (mounted && mounted.length > 0) {
            for (const p of mounted) {
                if (Array.isArray(p) && p.length >= 3) allPaths.push(p);
            }
        }
    }

    if (allPaths.length === 0) return null;

    const clipper = new ClipperLib.Clipper();
    clipper.AddPaths(allPaths, ClipperLib.PolyType.ptSubject, true);
    const solution = new ClipperLib.Paths();
    const ok = clipper.Execute(
        ClipperLib.ClipType.ctUnion,
        solution,
        ClipperLib.PolyFillType.pftNonZero,
        ClipperLib.PolyFillType.pftNonZero,
    );

    if (!ok || solution.length === 0) return null;

    const pathDs: string[] = [];
    for (let i = 0; i < solution.length; i++) {
        const pd = clipperPathToPathD(solution[i]);
        if (pd) pathDs.push(pd);
    }
    if (pathDs.length === 0) return null;

    let fill = defaultFill;
    for (const pl of placements) {
        const f = extractDefaultFillFromInnerSvg(pl.objectContent);
        if (f) {
            fill = f;
            break;
        }
    }

    return { pathD: pathDs.join(""), fill };
}

export function buildPathPopulateUnionSvgString(options: {
    pathSvgFullContent: string;
    showPaths: boolean;
    viewBoxAttr: string;
    placements: UnionPlacementPayload[];
    /** Used when union yields no geometry (fallback appearance). */
    defaultFill?: string;
}): string | null {
    const {
        pathSvgFullContent,
        showPaths,
        viewBoxAttr,
        placements,
        defaultFill = "#000000",
    } = options;

    const unioned = unionPlacementPayloads({ placements, defaultFill });
    if (!unioned) return null;

    const pathContent = showPaths
        ? pathSvgFullContent
              .replace(/<\?xml[^>]*\?>/gi, "")
              .replace(/<\/?svg[^>]*>/g, "")
              .trim()
        : "";

    const pathsSection = showPaths
        ? `  <g class="paths">
${pathContent}
  </g>`
        : "";

    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBoxAttr}">
${pathsSection}
  <g class="objects union">
    <path d="${unioned.pathD}" fill="${unioned.fill}" />
  </g>
</svg>`;
}
